import React, { useState, useEffect, useRef } from 'react';
import { 
  Package, 
  Barcode,
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  Save,
  History,
  Scan,
  X,
  Search,
  Eye,
  ChevronDown,
  RefreshCw,
  Camera
} from 'lucide-react';
import { useAuth } from '../utils/AuthContext';
import { useStore } from '../utils/StoreContext';
import { itemService, LocalItem } from '../services/itemService';
import { supabase } from '../utils/supabase';
import { MovementService } from '../services/movementService';
import { imageUploadService } from '../services/imageUploadService';
import { SessionService } from '../services/sessionService';
import { MovementSummary, DeliveryPersonDisplayInfo } from '../types';
import DeliveryPersonSelector from '../components/DeliveryPersonSelector';
import DeliveryPersonItemsModal from '../components/DeliveryPersonItemsModal';
import { DeliveryPersonItemListModal } from '../components/DeliveryPersonItemListModal';
import { DeliveryPersonScannerView } from '../components/DeliveryPersonScannerView';
import { DeliveryPersonService } from '../services/deliveryPersonService';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';

interface CartItem extends LocalItem {
  cartQuantity: number;
  unitPrice: number;
  unitCost: number;
  totalAmount: number;
}

const MovementRecorder: React.FC = () => {
  const { user } = useAuth();
  const { currentStore } = useStore();
  const [barcode, setBarcode] = useState('');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [recentMovements, setRecentMovements] = useState<MovementSummary[]>([]);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedImageItem, setSelectedImageItem] = useState<string | null>(null);
  const [showItemListDialog, setShowItemListDialog] = useState(false);
  const [availableItems, setAvailableItems] = useState<LocalItem[]>([]);
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [itemListLoading, setItemListLoading] = useState(false);
  const [currentSession, setCurrentSession] = useState<any>(null);
  const [movementType, setMovementType] = useState<'purchase_in' | 'adjustment' | 'delivery_out' | 'delivery_return' | 'opening_stock'>('delivery_out');
  const [showMovementTypeDropdown, setShowMovementTypeDropdown] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [storeSessions, setStoreSessions] = useState<any[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [showCreateSessionDialog, setShowCreateSessionDialog] = useState(false);
  const [newSessionMovementType, setNewSessionMovementType] = useState<'purchase_in' | 'adjustment' | 'delivery_out' | 'delivery_return' | 'opening_stock'>('delivery_out');
  const [newSessionRemarks, setNewSessionRemarks] = useState('');
  // Visual feedback states
  const [recentlyAddedItems, setRecentlyAddedItems] = useState<Set<string>>(new Set());
  const [recentlyUpdatedItems, setRecentlyUpdatedItems] = useState<Set<string>>(new Set());
  
  // Movement details dialog states
  const [showMovementDetailsDialog, setShowMovementDetailsDialog] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState<any>(null);
  const [movementDetails, setMovementDetails] = useState<any>(null);
  const [movementDetailsLoading, setMovementDetailsLoading] = useState(false);
  
  // Movement remarks state
  const [movementRemarks, setMovementRemarks] = useState('');
  
  // Camera scanner states
  const [showCameraScanner, setShowCameraScanner] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  
  // Delivery person state
  const [selectedDeliveryHash, setSelectedDeliveryHash] = useState<string | null>(null);
  const [selectedDeliveryPerson, setSelectedDeliveryPerson] = useState<DeliveryPersonDisplayInfo | null>(null);
  const [showDeliveryItemsModal, setShowDeliveryItemsModal] = useState(false);
  const [showDeliveryPersonItemListModal, setShowDeliveryPersonItemListModal] = useState(false);
  const [sessionDeliveryPersonItemsCount, setSessionDeliveryPersonItemsCount] = useState(0);
  
  // const [isLoadingSessionItems, setIsLoadingSessionItems] = useState(false); // Flag to prevent cart clearing during session load
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  
  // Visual feedback helper functions
  const triggerItemAdded = (itemBarcode: string) => {
    setRecentlyAddedItems(prev => new Set(prev).add(itemBarcode));
    // Play success sound
    playSuccessSound();
    // Remove the highlight after animation completes
    setTimeout(() => {
      setRecentlyAddedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemBarcode);
        return newSet;
      });
    }, 2000); // 2 second animation
  };

  const triggerItemUpdated = (itemBarcode: string) => {
    setRecentlyUpdatedItems(prev => new Set(prev).add(itemBarcode));
    // Remove the highlight after animation completes
    setTimeout(() => {
      setRecentlyUpdatedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemBarcode);
        return newSet;
      });
    }, 1500); // 1.5 second animation
  };

  // Get visual feedback classes for an item
  const getItemVisualFeedback = (itemBarcode: string) => {
    const isRecentlyAdded = recentlyAddedItems.has(itemBarcode);
    const isRecentlyUpdated = recentlyUpdatedItems.has(itemBarcode);
    const item = cart.find(cartItem => cartItem.barcode === itemBarcode);
    
    let baseClasses = "relative flex items-center gap-3 p-4 rounded-lg border transition-all duration-700 ease-in-out";
    
    // Priority 1: Recent action feedback (highest priority)
    if (isRecentlyAdded) {
      return `${baseClasses} bg-green-100 border-green-400 shadow-lg transform scale-105 animate-pulse`;
    } else if (isRecentlyUpdated) {
      return `${baseClasses} bg-blue-100 border-blue-400 shadow-md transform scale-102 animate-pulse`;
    }
    
    // Priority 2: Stock level indicators (when no recent actions)
    if (item) {
      const afterStock = getStockImpact(item.current_stock, item.cartQuantity);
      
      if (afterStock < 0) {
        // Negative stock - Red background
        return `${baseClasses} bg-red-50 border-red-300 hover:bg-red-100`;
      } else if (afterStock === 0) {
        // Zero stock - Orange background  
        return `${baseClasses} bg-orange-50 border-orange-300 hover:bg-orange-100`;
      } else if (afterStock <= 5) {
        // Low stock (1-5) - Yellow background
        return `${baseClasses} bg-yellow-50 border-yellow-300 hover:bg-yellow-100`;
      }
    }
    
    // Default: Normal stock levels
    return `${baseClasses} bg-gray-50 border-gray-200 hover:bg-gray-100`;
  };

  // Get quantity visual feedback classes
  const getQuantityFeedback = (itemBarcode: string) => {
    const isRecentlyUpdated = recentlyUpdatedItems.has(itemBarcode);
    const isRecentlyAdded = recentlyAddedItems.has(itemBarcode);
    
    if (isRecentlyAdded) {
      return "w-12 text-center font-bold text-green-700 transform scale-110 transition-all duration-500";
    } else if (isRecentlyUpdated) {
      return "w-12 text-center font-bold text-blue-700 transform scale-110 transition-all duration-500";
    } else {
      return "w-12 text-center font-medium";
    }
  };
  
  // Debug cart changes
  useEffect(() => {
    console.log('🛍️ Cart state changed:', {
      itemCount: cart.length,
      items: cart.map(item => ({ name: item.name, quantity: item.cartQuantity }))
    });
  }, [cart]);

  // Effect to load delivery person items count when session changes
  useEffect(() => {
    if (currentSession?.id) {
      loadSessionDeliveryPersonItemsCount();
    } else {
      setSessionDeliveryPersonItemsCount(0);
    }
  }, [currentSession?.id]);

  // Sound effects
  const playSuccessSound = () => {
    // Create success sound (higher pitch, pleasant)
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // High note
    oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1); // Higher note
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  };

  // Load session delivery person items count
  const loadSessionDeliveryPersonItemsCount = async () => {
    if (!currentSession?.id) {
      setSessionDeliveryPersonItemsCount(0);
      return;
    }

    try {
      const result = await DeliveryPersonService.getSessionDeliveryPersonItems(currentSession.id, user?.id);
      if (!result.error) {
        setSessionDeliveryPersonItemsCount(result.data?.length || 0);
      }
    } catch (error) {
      console.error('Error loading session delivery person items count:', error);
    }
  };

  // Handle when session delivery items are cleared from import dialog
  const handleSessionCleared = async () => {
    if (currentSession?.id) {
      console.log('🔄 Session cleared from import dialog, reloading session items...');
      // Reload the current session to sync cart with cleared items
      await loadSharedSession(currentSession.id);
      // Refresh delivery person items count
      await loadSessionDeliveryPersonItemsCount();
      setSuccess('Session delivery items cleared successfully');
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  // Delivery person selection handler
  const handleDeliveryPersonSelect = async (deliveryHash: string | null, displayInfo?: DeliveryPersonDisplayInfo) => {
    setSelectedDeliveryHash(deliveryHash || null);
    setSelectedDeliveryPerson(displayInfo || null);
    
    // Sync delivery person to database if selected and displayInfo available
    if (deliveryHash && displayInfo) {
      try {
        const deliveryPerson = {
          delivery_hash: deliveryHash,
          name: displayInfo.name || 'Unknown',
          code: displayInfo.code || '',
          active: true,
          deleted: false,
          phone_number: displayInfo.phone,
          avatar_url: displayInfo.avatar,
          telegram_id: undefined, // Not available in displayInfo
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        await DeliveryPersonService.syncDeliveryPersonToDatabase(deliveryPerson);
        console.log('✅ Delivery person synced to database:', displayInfo.name);
      } catch (error) {
        console.error('❌ Failed to sync delivery person to database:', error);
        // Don't block the selection if sync fails
      }
    }
    
    // Save delivery hash to current session if session exists
    if (currentSession?.id) {
      try {
        await SessionService.saveDeliveryHashToSession(
          currentSession.id,
          deliveryHash,
          user?.id
        );
        console.log('✅ Delivery hash saved to session:', deliveryHash);
      } catch (error) {
        console.error('❌ Failed to save delivery hash to session:', error);
      }
    }
  };

  // Clear delivery person when movement type changes to non-delivery types
  useEffect(() => {
    if (movementType !== 'delivery_out' && movementType !== 'delivery_return') {
      setSelectedDeliveryHash(null);
      setSelectedDeliveryPerson(null);
    }
  }, [movementType]);

  const playErrorSound = () => {
    // Create error sound (lower pitch, attention-grabbing)
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.setValueAtTime(400, audioContext.currentTime); // Low note
    oscillator.frequency.setValueAtTime(300, audioContext.currentTime + 0.1); // Lower note
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.4);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.4);
  };

  // Camera scanner functions
  const startCameraScanner = async () => {
    try {
      setCameraLoading(true);
      setCameraError(null);
      setShowCameraScanner(true);

      // Initialize the code reader
      codeReaderRef.current = new BrowserMultiFormatReader();

      // Wait for the modal to be rendered
      setTimeout(async () => {
        if (videoRef.current && codeReaderRef.current) {
          try {
            await codeReaderRef.current.decodeFromVideoDevice(
              null, // Use default camera
              videoRef.current,
              (result, error) => {
                if (result) {
                  // Successfully scanned a barcode
                  const scannedCode = result.getText();
                  setBarcode(scannedCode);
                  stopCameraScanner();
                  // Trigger the barcode submission
                  setTimeout(() => {
                    if (barcodeInputRef.current) {
                      barcodeInputRef.current.form?.requestSubmit();
                    }
                  }, 100);
                }
                if (error && !(error instanceof NotFoundException)) {
                  console.warn('Camera scanner error:', error);
                }
              }
            );
            setCameraLoading(false);
          } catch (err) {
            console.error('Camera access error:', err);
            setCameraError('Unable to access camera. Please check permissions.');
            setCameraLoading(false);
          }
        }
      }, 100);
    } catch (err) {
      console.error('Camera scanner initialization error:', err);
      setCameraError('Failed to initialize camera scanner.');
      setCameraLoading(false);
    }
  };

  const stopCameraScanner = () => {
    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
      codeReaderRef.current = null;
    }
    setShowCameraScanner(false);
    setCameraLoading(false);
    setCameraError(null);
  };

  // Focus barcode input on component mount
  useEffect(() => {
    if (barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, []);

  // Cleanup camera scanner on unmount
  useEffect(() => {
    return () => {
      if (codeReaderRef.current) {
        codeReaderRef.current.reset();
      }
    };
  }, []);

    // Prevent double-loading with refs and debouncing
  const isLoadingRef = useRef(false);
  const loadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Load recent movements and session when store/movement type changes (debounced)
  useEffect(() => {
    if (currentStore && user) {
      console.log('📍 useEffect triggered (debounced):', { 
        store: currentStore?.name, 
        movementType, 
        userId: user?.id?.slice(0,8),
        isLoading: isLoadingRef.current
      });
      
      // Clear any existing timeout
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
      
      // Debounce to prevent rapid successive calls
      loadTimeoutRef.current = setTimeout(async () => {
        if (isLoadingRef.current) {
          console.log('🚫 Skipping load - already in progress');
          return;
        }
        
        isLoadingRef.current = true;
        
        try {
          console.log('🚀 Starting data load...');
          
          // Load recent movements (non-blocking)
          loadRecentMovements();
          
          // Smart session loading: only reload if necessary
          const needsNewSession = !currentSession || 
            currentSession.store_id !== currentStore.id ||
            currentSession.movement_type !== movementType ||
            currentSession.created_by !== user.id;
            
          if (needsNewSession) {
            console.log('🔄 Loading session for new context', {
              storeChanged: currentSession?.store_id !== currentStore.id,
              userChanged: currentSession?.created_by !== user.id,
              movementTypeChanged: currentSession?.movement_type !== movementType,
              currentSessionMovement: currentSession?.movement_type,
              currentMovementType: movementType
            });
            
            // Only clear cart if changing stores/users (preserve items for movement type changes)
            const changingStore = currentSession?.store_id !== currentStore.id;
            const changingUser = currentSession?.created_by !== user.id;
            
            if (changingStore || changingUser) {
              console.log('🗑️ Clearing cart - store/user changed');
              setCart([]);
            }
            
            await loadOrCreateSession();
          } else {
            console.log('✅ Session context unchanged - keeping current state:', {
              sessionId: currentSession.id?.slice(0,8),
              cartItems: cart.length,
              sessionItems: currentSession.total_items
            });
            
            // If session has items but cart is empty, reload items (this fixes navigation issue)
            if (currentSession.total_items > 0 && cart.length === 0) {
              console.log('🔄 FIXING: Session has items but cart empty - loading items...');
              await loadOrCreateSession();
            }
          }
        } catch (error) {
          console.error('❌ Error in useEffect data loading:', error);
        } finally {
          isLoadingRef.current = false;
        }
      }, 200); // 200ms debounce
    }
    
    // Cleanup timeout on unmount
    return () => {
      if (loadTimeoutRef.current) {
        clearTimeout(loadTimeoutRef.current);
      }
    };
  }, [currentStore?.id, user?.id]); // Remove movementType to prevent constant reloading
  
  // Separate useEffect for movement type changes (doesn't reload entire session)
  useEffect(() => {
    if (currentSession && currentSession.movement_type !== movementType) {
      console.log('🔄 Movement type changed - need new session:', {
        from: currentSession.movement_type,
        to: movementType
      });
      
      // Only reload session if movement type actually changed and we have context
      if (currentStore && user && !isLoadingRef.current) {
        loadOrCreateSession();
      }
    }
  }, [movementType]);

  const loadRecentMovements = async () => {
    if (!currentStore || !user) return;
    
    try {
      console.log('🔄 Loading movements with store_id:', currentStore.id, 'type:', typeof currentStore.id);
      
      // Use fixed RPC function with proper parameters
      const { data, error } = await supabase.rpc('get_recent_movements_v2', {
        p_store_id: currentStore.id,
        p_user_id: user.id,
        p_movement_type: null, // Show all movement types
        p_limit: 10
      });
      
      if (error || !data?.success) {
        console.error('Error loading movements via RPC:', error || data?.error);
        setRecentMovements([]);
      } else {
        console.log('✅ Recent movements loaded via RPC:', data.data?.length || 0);
        setRecentMovements(data.data || []);
      }
    } catch (error) {
      console.error('Error loading movements:', error);
      setRecentMovements([]);
    }
  };

  // Enhanced session management functions
  const loadOrCreateSession = async () => {
    if (!currentStore || !user) {
      console.log('Missing requirements for session:', { currentStore: !!currentStore, user: !!user });
      return;
    }
    
    setSessionLoading(true);
    setError(null);
    
    try {
      console.log('Loading existing or creating session with:', {
        userId: user.id,
        storeId: currentStore.id,
        movementType: movementType
      });
      
      // First try to get existing session (for session persistence)
      const result = await SessionService.getOrCreateSession(user.id, currentStore.id, movementType);
      console.log('loadOrCreateSession result:', result);

      if (!result.success) {
        console.error('Session loading failed:', result.error);
        setError(`Failed to load session: ${result.error}`);
      } else if (result.data) {
        console.log('Setting session data:', result.data.session);
        
        // Synchronize movement type BEFORE setting session to prevent infinite loops
        if (result.data.session.movement_type !== movementType) {
          console.log('🔄 Syncing movement type to prevent loop:', {
            sessionType: result.data.session.movement_type,
            currentType: movementType
          });
          setMovementType(result.data.session.movement_type);
        }
        
        setCurrentSession(result.data.session);
        
        // Restore delivery person from session if available
        if (result.data.session.delivery_hash) {
          setSelectedDeliveryHash(result.data.session.delivery_hash);
          
          // Load delivery person display info from API
          try {
            const deliveryResult = await DeliveryPersonService.getDeliveryPersonByHash(result.data.session.delivery_hash);
            if (deliveryResult.data && !deliveryResult.error) {
              const displayInfo = DeliveryPersonService.getDeliveryPersonDisplayInfo(deliveryResult.data);
              setSelectedDeliveryPerson(displayInfo);
              console.log('✅ Delivery person restored from session:', displayInfo.name);
            } else {
              console.warn('Could not load delivery person info:', deliveryResult.error);
              // Keep the hash but clear the display info
              setSelectedDeliveryPerson(null);
            }
          } catch (error) {
            console.warn('Exception loading delivery person info:', error);
            setSelectedDeliveryPerson(null);
          }
        } else {
          setSelectedDeliveryHash(null);
          setSelectedDeliveryPerson(null);
        }
        
        // Load cart from session items (for session persistence)
        console.log('Full result structure:', JSON.stringify(result, null, 2));
        console.log('result.data:', result.data);
        console.log('result.data.items:', result.data?.items);
        
        const sessionItems = result.data?.items || [];
        console.log('RAW session items received:', sessionItems);
        console.log('Number of session items:', sessionItems.length);
        
        // SessionService should handle all item loading - no Dashboard fallback needed
        if (sessionItems.length === 0 && result.data?.session?.total_items > 0) {
          console.warn('⚠️ MISMATCH: SessionService failed to return items - this should not happen with the fixed queries');
          console.log('Session total_items:', result.data.session.total_items);
          console.log('Items array:', sessionItems);
          // Dashboard fallback disabled to prevent cart duplication
        }
        
        if (sessionItems.length > 0) {
          console.log('🔄 Enriching session items with external API data...');
          const enrichedCartItems: CartItem[] = [];
          
          for (const sessionItem of sessionItems) {
            console.log('🔍 Processing session item:', sessionItem);
            
            let enrichedItem = sessionItem.item;
            
            // If item data is incomplete, try to fetch from external API
            // Use item data directly from Supabase database (no external API enrichment)
            console.log('📦 Using item from Supabase database:', {
              name: sessionItem.item?.name,
              sku: sessionItem.item?.sku,
              description: sessionItem.item?.description,
              hasImage: !!sessionItem.item?.images?.length
            });
            
            const cartItem: CartItem = {
              ...enrichedItem,
              cartQuantity: sessionItem.quantity,
              unitPrice: Number(sessionItem.unit_price),
              unitCost: Number(sessionItem.unit_cost),
              totalAmount: Number(sessionItem.total_amount),
              // Stock levels are store-specific from RPC (get_or_create_flexible_session handles store filtering)
              current_stock: sessionItem.current_stock || 0,
              average_cost: sessionItem.average_cost || 0,
              total_value: sessionItem.total_value || 0
            };
            
            console.log('✅ Final enriched cart item:', {
              name: cartItem.name,
              sku: cartItem.sku,
              barcode: cartItem.barcode,
              images: cartItem.images?.length || 0,
              hasAllDetails: !!(cartItem.name && cartItem.sku && cartItem.barcode)
            });
            
            enrichedCartItems.push(cartItem);
          }
          
          console.log('Setting cart with enriched items:', enrichedCartItems);
          // Sort items by creation time (most recent first) to maintain consistent ordering
          const sortedCartItems = enrichedCartItems.sort((a, b) => {
            // If items have creation timestamps, use them; otherwise maintain current order
            return 0; // Keep current order for now, but structure is ready for timestamp sorting
          });
          setCart(sortedCartItems);
          
          // Use a ref to track if we're in the middle of loading session items
          // Double-check that cart was set correctly after a brief delay
          setTimeout(() => {
            console.log('🔍 Cart verification after session load:');
            console.log('- Expected items:', enrichedCartItems.length);
            console.log('- Actual cart state:', cart.length);
            console.log('- Session total_items:', result.data?.session?.total_items ?? 0);
            
            if (cart.length !== enrichedCartItems.length) {
              console.warn('⚠️ Cart length mismatch! Re-setting items...');
              setCart([...enrichedCartItems]); // Force re-set with spread operator
            } else {
              console.log('✅ Cart items loaded correctly');
            }
          }, 500);
          
          setSuccess(`🔄 Resumed session: ${result.data.session.session_name} (${sessionItems.length} items)`);
        } else {
          console.log('No items in session, setting empty cart');
          setCart([]);
        }
        
        console.log('Session loaded/resumed successfully with', sessionItems.length, 'items');
        setTimeout(() => setSuccess(null), 4000);
      }
    } catch (error) {
      console.error('Error with session:', error);
      setError(`Session error: ${error}`);
    } finally {
      setSessionLoading(false);
      // setIsLoadingSessionItems(false); // Commented out since state variable is disabled
    }
  };

  // Create a new session manually
  const createNewSession = async (movementTypeOverride?: string, remarks?: string) => {
    if (!currentStore || !user) {
      setError('Missing store or user information');
      return;
    }
    
    setSessionLoading(true);
    try {
      const result = await SessionService.createNewSession(
        user.id, 
        currentStore.id, 
        movementTypeOverride || movementType,
        remarks
      );
      
      if (result.success && result.data) {
        setCurrentSession(result.data.session);
        setCart([]); // Clear cart for new session
        setSuccess('New session created successfully');
        setTimeout(() => setSuccess(null), 3000);
        console.log('New session created:', result.data.session.id);
      } else {
        setError(`Failed to create new session: ${result.error}`);
      }
    } catch (error) {
      console.error('Error creating new session:', error);
      setError('Failed to create new session');
    } finally {
      setSessionLoading(false);
    }
  };

  // Close current session
  const closeCurrentSession = async () => {
    if (!currentSession) {
      setError('No active session to close');
      return;
    }
    
    try {
      const result = await SessionService.closeSession(currentSession.id);
      if (result.success) {
        setCurrentSession(null);
        setCart([]);
        setSuccess('Session closed successfully');
        setTimeout(() => setSuccess(null), 3000);
        console.log('Session closed:', currentSession.id);
      } else {
        setError(`Failed to close session: ${result.error}`);
      }
    } catch (error) {
      console.error('Error closing session:', error);
      setError('Failed to close session');
    }
  };

  const ensureSessionExists = async () => {
    if (currentSession) {
      console.log('Session already exists:', currentSession.id);
      return currentSession;
    }
    
    // Enhanced validation
    if (!user || !user.id) {
      console.error('User not authenticated:', user);
      setError('Please login again - user not authenticated');
      return null;
    }
    
    if (!currentStore || !currentStore.id) {
      console.error('No store selected:', currentStore);
      setError('Please select a store first');
      return null;
    }
    
    if (!movementType) {
      console.error('No movement type selected:', movementType);
      setError('Please select a movement type');
      return null;
    }
    
    console.log('Creating session with validated params:', { 
      userId: user.id, 
      storeId: currentStore.id, 
      movementType: movementType
    });
    
    setSessionLoading(true);
    
    try {
      // Use SessionService instead of direct RPC call
      const result = await SessionService.getOrCreateSession(
        user.id,
        currentStore.id,
        movementType
      );
      
      console.log('SessionService response:', result);
      
      if (!result.success) {
        console.error('SessionService Error:', result.error);
        setError(`Database error: ${result.error}`);
        return null;
      }
      
      if (result.data?.session) {
        const newSession = result.data.session;
        console.log('Setting new session:', newSession);
        setCurrentSession(newSession);
        return newSession;
      } else {
        console.error('Session creation failed:', result);
        setError(`Session creation failed: ${result.error || 'Unknown error'}`);
        return null;
      }
    } catch (error) {
      console.error('Exception in ensureSessionExists:', error);
      setError(`Critical error: ${error}`);
      return null;
    } finally {
      setSessionLoading(false);
    }
  };

  const saveItemToSession = async (item: CartItem) => {
    // Ensure session exists before saving
    const session = await ensureSessionExists();
    if (!session || !currentStore || !user) {
      console.warn('No session, store, or user available, skipping save');
      return;
    }
    
    try {
      // Use SessionService with updated signature
      const result = await SessionService.saveItemToSession(
        user.id,
        currentStore.id,
        movementType,
        item.id,
        item.cartQuantity,
        item.unitPrice,
        item.unitCost,
        `MOV-${Date.now()}`
      );

      if (!result.success) {
        console.error('Error saving item to session:', result.error);
      }
    } catch (error) {
      console.error('Error saving to session:', error);
    }
  };

  const removeItemFromSession = async (itemId: string) => {
    if (!currentSession || !currentStore || !user) return;
    
    try {
      // Use SessionService with updated signature
      const result = await SessionService.removeItemFromSession(
        user.id,
        currentStore.id,
        movementType,
        itemId
      );

      if (!result.success) {
        console.error('Error removing item from session:', result.error);
      }
    } catch (error) {
      console.error('Error removing from session:', error);
    }
  };

  const getMovementTypeConfig = () => {
    const configs = {
      'purchase_in': { label: 'Purchase In', icon: '📦', color: 'green', description: 'Stock received from suppliers' },
      'adjustment': { label: 'Stock Adjustment', icon: '⚖️', color: 'blue', description: 'Manual stock corrections' },
      'delivery_out': { label: 'Delivery Out', icon: '🚚', color: 'orange', description: 'Items sent out for delivery' },
      'delivery_return': { label: 'Delivery Return', icon: '↩️', color: 'purple', description: 'Returned delivery items' },
      'opening_stock': { label: 'Opening Stock', icon: '🏗️', color: 'gray', description: 'Initial stock setup' }
    };
    return configs[movementType];
  };

  // Calculate stock impact based on movement type
  const getStockImpact = (currentStock: number, quantity: number) => {
    switch (movementType) {
      case 'purchase_in':
      case 'delivery_return':
      case 'opening_stock':
        return currentStock + quantity; // Stock increases
      case 'delivery_out':
        return currentStock - quantity; // Stock decreases
      case 'adjustment':
        return currentStock + quantity; // Can be positive or negative
      default:
        return currentStock - quantity;
    }
  };

  const loadStoreSessions = async () => {
    if (!currentStore || !user) return;
    
    setSessionsLoading(true);
    try {
      // Use isolated session loading for strict store separation
      const result = await SessionService.getStoreSessionsIsolated(currentStore.id, user.id);

      if (result.error) {
        console.error('Error loading store sessions:', result.error);
        setError(`Failed to load sessions: ${result.error}`);
      } else if (result.success) {
        const sessions = result.data || [];
        console.log('Loaded isolated sessions for store:', currentStore.name, 'Count:', sessions.length);
        setStoreSessions(sessions);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
      setError('Failed to load sessions');
    } finally {
      setSessionsLoading(false);
    }
  };

  const loadSharedSession = async (sessionId: string) => {
    console.log('Loading session:', sessionId);
    setSessionsLoading(true);
    
    try {
      const result = await SessionService.loadSessionItems(sessionId);
      console.log('Load session result:', result);

      if (!result.success) {
        console.error('Session load failed:', result.error);
        setError(`Failed to load session: ${result.error}`);
      } else if (result.data) {
        const sessionData = result.data;
        console.log('Session data received:', sessionData);
        
        // Update current session info
        setCurrentSession(sessionData.session);
        setMovementType(sessionData.session.movement_type);
        
        // Restore delivery person from session if available
        if (sessionData.session.delivery_hash) {
          setSelectedDeliveryHash(sessionData.session.delivery_hash);
        } else {
          setSelectedDeliveryHash(null);
          setSelectedDeliveryPerson(null);
        }
        
        // Load cart from session items
        const sessionItems = sessionData.items || [];
        console.log('Session items to load:', sessionItems);
        
        if (sessionItems.length > 0) {
          console.log('🔄 Enriching shared session items with external API data...');
          const enrichedCartItems: CartItem[] = [];
          
          for (const sessionItem of sessionItems) {
            console.log('🔍 Processing shared session item:', sessionItem);
            
            let enrichedItem = sessionItem.item;
            
            // If item data is incomplete, try to fetch from external API
            // Use item data directly from Supabase database (no external API enrichment)
            console.log('📦 Using shared session item from Supabase database:', {
              name: sessionItem.item?.name,
              sku: sessionItem.item?.sku,
              description: sessionItem.item?.description,
              hasImage: !!sessionItem.item?.images?.length
            });
            
            const cartItem = {
              ...enrichedItem,
              cartQuantity: sessionItem.quantity,
              unitPrice: sessionItem.unit_price,
              unitCost: sessionItem.unit_cost,
              totalAmount: sessionItem.total_amount,
              // Stock levels are store-specific from RPC (get_store_sessions_isolated handles store filtering)
              current_stock: sessionItem.current_stock,
              average_cost: sessionItem.average_cost,
              total_value: sessionItem.total_value
            };
            
            console.log('✅ Final enriched shared session cart item:', {
              name: cartItem.name,
              sku: cartItem.sku,
              barcode: cartItem.barcode,
              images: cartItem.images?.length || 0,
              hasAllDetails: !!(cartItem.name && cartItem.sku && cartItem.barcode)
            });
            
            enrichedCartItems.push(cartItem);
          }
          
          console.log('Setting cart with enriched shared session items:', enrichedCartItems);
          // Sort items by creation time (most recent first) to maintain consistent ordering
          const sortedCartItems = enrichedCartItems.sort((a, b) => {
            // If items have creation timestamps, use them; otherwise maintain current order
            return 0; // Keep current order for now, but structure is ready for timestamp sorting
          });
          setCart(sortedCartItems);
          setSuccess(`✅ Loaded session: ${sessionData.session.session_name} (${sessionItems.length} items)`);
        } else {
          setCart([]);
          setSuccess(`📋 Loaded empty session: ${sessionData.session.session_name}`);
        }
        
        setShowSessionDialog(false);
        setTimeout(() => setSuccess(null), 4000);
        
        // Auto-focus scan field after loading session
        setTimeout(() => {
          if (barcodeInputRef.current) {
            barcodeInputRef.current.focus();
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error loading session:', error);
      setError('Failed to load session');
    } finally {
      setSessionsLoading(false);
    }
  };

  // Handle closing a specific session from the dialog (completely deletes it)
  const handleCloseSession = async (sessionId: string) => {
    try {
      const result = await SessionService.closeSession(sessionId);
      if (result.success) {
        // If closing current session, clear it
        if (currentSession?.id === sessionId) {
          setCurrentSession(null);
          setCart([]);
        }
        // Reload sessions list (will automatically filter out deleted session)
        await loadStoreSessions();
        setSuccess('🗑️ Session deleted completely');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(`Failed to delete session: ${result.error}`);
      }
    } catch (error) {
      console.error('Error deleting session:', error);
      setError('Failed to delete session');
    }
  };

  // Handle clearing items from a specific session (if session becomes empty, delete it)
  const handleClearSessionItems = async (sessionId: string) => {
    try {
      const result = await SessionService.clearSession(sessionId);
      if (result.success) {
        // If clearing current session, clear cart too
        if (currentSession?.id === sessionId) {
          setCart([]);
        }
        
        // Check if session is now empty and delete it completely
        // Use RPC to get session info
        if (!user?.id) return;
        
        const { data: sessionData } = await supabase.rpc('get_user_movement_sessions', {
          p_user_id: user.id
        });
        
        const sessionCheck = sessionData?.find((s: any) => s.id === sessionId);
          
        if (sessionCheck?.total_items === 0) {
          // Delete empty session
          await SessionService.closeSession(sessionId);
          if (currentSession?.id === sessionId) {
            setCurrentSession(null);
          }
          setSuccess('🗑️ Session cleared and deleted (was empty)');
        } else {
          setSuccess('Session items cleared successfully');
        }
        
        // Reload sessions list (will filter out deleted session)
        await loadStoreSessions();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(`Failed to clear session items: ${result.error}`);
      }
    } catch (error) {
      console.error('Error clearing session items:', error);
      setError('Failed to clear session items');
    }
  };

  const scanBarcode = async (barcodeValue: string) => {
    if (!barcodeValue.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // First search in local Supabase database (faster)
      const localResult = await itemService.searchLocalItems(barcodeValue, currentStore?.id);
      
      if (localResult.error) {
        setError(`Error searching local items: ${localResult.error}`);
        return;
      }

      // Find exact barcode match in local results
      const localMatch = localResult.data?.find(item => 
        item.barcode?.toLowerCase() === barcodeValue.toLowerCase() || 
        item.sku?.toLowerCase() === barcodeValue.toLowerCase()
      );

      if (localMatch) {
        // Found in local database - use this item
        const existingCartItem = cart.find(cartItem => cartItem.barcode === localMatch.barcode);
        
        if (existingCartItem) {
          // Increase quantity
          updateCartItemQuantity(existingCartItem.barcode, existingCartItem.cartQuantity + 1);
          // Trigger visual feedback for updated item
          triggerItemUpdated(existingCartItem.barcode);
        } else {
          // Add new item to cart
          const cartItem: CartItem = {
            ...localMatch,
            cartQuantity: 1,
            unitPrice: localMatch.average_cost || 0,
            unitCost: localMatch.average_cost || 0,
            totalAmount: localMatch.average_cost || 0
          };
          
          // Ensure session exists before adding item
          const session = await ensureSessionExists();
          if (session) {
            // Add new item to the TOP of the cart (most recent first)
            setCart(prev => [cartItem, ...prev]);
            // Auto-save to session immediately after state update
            setTimeout(() => saveItemToSession(cartItem), 100);
            // Trigger visual feedback for newly added item
            triggerItemAdded(cartItem.barcode);
          } else {
            setError('Could not create session. Please try again.');
            return;
          }
        }
        
        // Play success sound
        playSuccessSound();
        
        setBarcode(''); // Clear barcode input only on success
        // Use setTimeout to ensure focus happens after React state updates
        setTimeout(() => {
          if (barcodeInputRef.current) {
            barcodeInputRef.current.focus();
          }
        }, 100);
        return;
      }

      // If not found locally, show error but keep text for correction
      setError(`Item not found in local database for: ${barcodeValue}. Please add it to inventory first.`);
      
      // Play error sound
      playErrorSound();
      
      // DON'T clear barcode - keep text so user can correct it
      // Just refocus to select all text for easy correction
      setTimeout(() => {
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus();
          barcodeInputRef.current.select(); // Select all text for easy correction
        }
      }, 100);
      
    } catch (error) {
      setError('Failed to scan barcode');
      
      // Play error sound
      playErrorSound();
      
      // Keep barcode text for correction, just refocus and select
      setTimeout(() => {
        if (barcodeInputRef.current) {
          barcodeInputRef.current.focus();
          barcodeInputRef.current.select(); // Select all text for easy correction
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleBarcodeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    scanBarcode(barcode);
  };

  const updateCartItemQuantity = (barcode: string, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeCartItem(barcode);
      return;
    }
    
    setCart(prev => prev.map(item => {
      if (item.barcode === barcode) {
        const updatedItem = { ...item, cartQuantity: newQuantity, totalAmount: newQuantity * item.unitPrice };
        // Auto-save to session (async, but don't block UI)
        setTimeout(() => saveItemToSession(updatedItem), 100);
        // Trigger visual feedback for updated item
        triggerItemUpdated(barcode);
        return updatedItem;
      }
      return item;
    }));
  };

  const updateCartItemPrice = (barcode: string, newPrice: number) => {
    setCart(prev => prev.map(item => {
      if (item.barcode === barcode) {
        const updatedItem = { ...item, unitPrice: newPrice, totalAmount: item.cartQuantity * newPrice };
        // Auto-save to session (async, but don't block UI)
        setTimeout(() => saveItemToSession(updatedItem), 100);
        return updatedItem;
      }
      return item;
    }));
  };

  const removeCartItem = (barcode: string) => {
    const itemToRemove = cart.find(item => item.barcode === barcode);
    if (itemToRemove) {
      // Remove from session
      removeItemFromSession(itemToRemove.id);
    }
    setCart(prev => prev.filter(item => item.barcode !== barcode));
  };

  const clearCart = async () => {
    if (currentSession) {
      // Completely delete the session (not just clear items)
      const result = await SessionService.closeSession(currentSession.id);
      if (result.success) {
        setCurrentSession(null); // Clear current session state
        setSuccess('🗑️ Session deleted - new one will be created when adding items');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(`Failed to delete session: ${result.error}`);
      }
    }
    setCart([]);
    setError(null);
    
    // Refocus on barcode input after clearing
    setTimeout(() => {
      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    }, 100);
  };

  const openImageModal = (imageUrl: string, itemName: string) => {
    setSelectedImage(imageUrl);
    setSelectedImageItem(itemName);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
    setSelectedImageItem(null);
  };

  const loadAvailableItems = async () => {
    if (!currentStore) return;
    
    setItemListLoading(true);
    try {
      const result = await itemService.searchLocalItems(itemSearchTerm, currentStore.id, 1, 50);
      if (result.error) {
        console.error('Error loading items:', result.error);
      } else {
        setAvailableItems(result.data || []);
      }
    } catch (error) {
      console.error('Error loading items:', error);
    } finally {
      setItemListLoading(false);
    }
  };

  const addItemFromList = async (item: LocalItem) => {
    const existingCartItem = cart.find(cartItem => cartItem.barcode === item.barcode);
    
    if (existingCartItem) {
      // Increase quantity
      updateCartItemQuantity(existingCartItem.barcode, existingCartItem.cartQuantity + 1);
      // Trigger visual feedback for updated item
      triggerItemUpdated(existingCartItem.barcode);
    } else {
      // Add new item to cart
      const cartItem: CartItem = {
        ...item,
        cartQuantity: 1,
        unitPrice: item.average_cost || 0,
        unitCost: item.average_cost || 0,
        totalAmount: item.average_cost || 0
      };
      const session = await ensureSessionExists();
      if (session) {
        // Add new item to the TOP of the cart (most recent first)
        setCart(prev => [cartItem, ...prev]);
        // Auto-save to session immediately after state update
        setTimeout(() => saveItemToSession(cartItem), 100);
        // Trigger visual feedback for newly added item
        triggerItemAdded(cartItem.barcode);
      } else {
        setError('Could not create session. Please try again.');
        return;
      }
    }
    
    // Play success sound
    playSuccessSound();
    
    setShowItemListDialog(false);
    // Refocus on barcode input with slight delay
    setTimeout(() => {
      if (barcodeInputRef.current) {
        barcodeInputRef.current.focus();
      }
    }, 100);
  };

  // Load items when dialog opens or search term changes
  useEffect(() => {
    if (showItemListDialog) {
      loadAvailableItems();
    }
  }, [showItemListDialog, itemSearchTerm, currentStore]);

  // Clear cart and refresh data when store changes
  useEffect(() => {
    if (currentStore) {
      console.log(`🏪 Store changed to: ${currentStore.name}`);
      
      // Clear cart when switching stores (different store = different inventory)
      setCart([]);
      
      // Clear available items to force refresh with new store's stock
      setAvailableItems([]);
      
      // Clear any cached data that might have old stock levels
      setItemSearchTerm('');
      
      // Clear visual feedback states
      setRecentlyAddedItems(new Set());
      setRecentlyUpdatedItems(new Set());
      
      // Clear movement remarks
      setMovementRemarks('');
      
      // Clear delivery person selection
      setSelectedDeliveryHash(null);
      setSelectedDeliveryPerson(null);
      
      // Clear any error states
      setError(null);
      
      console.log(`✅ Cleared cart and cached data for store switch to: ${currentStore.name}`);
    }
  }, [currentStore?.id]); // Only trigger when store ID actually changes

  // Refresh stock levels for current cart items (manual refresh)
  const refreshCartStockLevels = async () => {
    if (!currentStore || !user || cart.length === 0) return;
    
    setLoading(true);
    try {
      // Reload the current session to get fresh stock levels
      await loadOrCreateSession();
      console.log(`🔄 Refreshed stock levels for ${cart.length} items in store: ${currentStore.name}`);
    } catch (error) {
      console.error('Error refreshing stock levels:', error);
      setError('Failed to refresh stock levels');
    } finally {
      setLoading(false);
    }
  };

  // Handle movement details dialog
  const handleMovementClick = async (movement: any) => {
    if (!user) return;
    
    setSelectedMovement(movement);
    setShowMovementDetailsDialog(true);
    setMovementDetailsLoading(true);
    setMovementDetails(null);
    
    try {
      const { data, error } = await supabase.rpc('get_movement_details', {
        p_movement_id: movement.movement_id,
        p_user_id: user.id
      });
      
      if (error || !data?.success) {
        console.error('Error loading movement details:', error || data?.error);
        setError(`Failed to load movement details: ${error?.message || data?.error}`);
      } else {
        setMovementDetails(data.data);
      }
    } catch (error) {
      console.error('Error loading movement details:', error);
      setError('Failed to load movement details');
    } finally {
      setMovementDetailsLoading(false);
    }
  };

  const closeMovementDetailsDialog = () => {
    setShowMovementDetailsDialog(false);
    setSelectedMovement(null);
    setMovementDetails(null);
    setMovementDetailsLoading(false);
  };

  const getTotalAmount = () => {
    return cart.reduce((total, item) => total + item.totalAmount, 0);
  };

  const getTotalItems = () => {
    return cart.reduce((total, item) => total + item.cartQuantity, 0);
  };

  const saveTransaction = async () => {
    if (cart.length === 0) {
      setError('No items in cart to save as movement entry');
      return;
    }

    if (!currentStore || !user) {
      setError('Store or user not available');
      return;
    }

    // Validate delivery person for delivery movements
    if ((movementType === 'delivery_out' || movementType === 'delivery_return') && !selectedDeliveryHash) {
      const actionText = movementType === 'delivery_out' ? 'outgoing deliveries' : 'delivery returns';
      setError(`Please select a delivery person for ${actionText}`);
      return;
    }

    // Check for negative stock warnings based on movement type
    const negativeStockItems = cart.filter(item => getStockImpact(item.current_stock, item.cartQuantity) < 0);
    if (negativeStockItems.length > 0) {
      const itemNames = negativeStockItems.map(item => item.name).join(', ');
      if (!window.confirm(`Warning: The following items will have negative stock after this movement:\n\n${itemNames}\n\nDo you want to continue anyway?`)) {
        return;
      }
    }

    setSaving(true);
    setError(null);
    
    try {
      // Ensure session exists before saving
      const session = await ensureSessionExists();
      if (!session) {
        setError('Could not create session for saving movement');
        setSaving(false);
        return;
      }

      // Use SessionService instead of direct RPC call
      const result = await SessionService.convertSessionToMovement(
        session.id,
        movementRemarks || `Movement Entry - ${cart.length} items`,
        selectedDeliveryHash || undefined
      );

      if (!result.success) {
        setError(`Failed to save movement entry: ${result.error}`);
      } else {
        // Session is automatically DELETED after successful save
        setSuccess(`✅ Movement saved! Reference: ${result.data?.reference_number}`);
        setCurrentSession(null); // Clear session state
        setCart([]); // Clear cart
        setMovementRemarks(''); // Clear remarks
        loadRecentMovements(); // Refresh movement history
        
        // Clear success message after delay
        setTimeout(() => setSuccess(null), 5000);
        
        // Focus back to scanner for next transaction
        setTimeout(() => {
          if (barcodeInputRef.current) {
            barcodeInputRef.current.focus();
          }
        }, 100);
      }
    } catch (error) {
      setError('Failed to save movement entry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 min-h-screen lg:h-auto w-full max-w-none">
      {/* Control Panel */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Movement Type Selector */}
          <div className="relative">
            <button
              onClick={() => setShowMovementTypeDropdown(!showMovementTypeDropdown)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
            >
              <span className="text-lg">{getMovementTypeConfig().icon}</span>
              <span className="hidden sm:inline">{getMovementTypeConfig().label}</span>
              <ChevronDown className="w-4 h-4 text-gray-400" />
            </button>
            
            {showMovementTypeDropdown && (
              <div className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                {[
                  { value: 'delivery_out', label: 'Delivery Out', icon: '📤', color: 'text-orange-600' },
                  { value: 'purchase_in', label: 'Purchase In', icon: '📥', color: 'text-green-600' },
                  { value: 'adjustment', label: 'Stock Adjustment', icon: '⚖️', color: 'text-blue-600' },
                  { value: 'delivery_return', label: 'Delivery Return', icon: '🔄', color: 'text-purple-600' },
                  { value: 'opening_stock', label: 'Opening Stock', icon: '🏪', color: 'text-gray-600' }
                ].map((type) => (
                  <button
                    key={type.value}
                    onClick={() => {
                      setMovementType(type.value as any);
                      setShowMovementTypeDropdown(false);
                    }}
                    className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 transition-colors ${
                      movementType === type.value ? 'bg-blue-50 border-r-2 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">{type.icon}</span>
                      <span className={`font-medium ${movementType === type.value ? 'text-blue-700' : 'text-gray-700'}`}>
                        {type.label}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Browse Items Button */}
          <button
            onClick={() => setShowItemListDialog(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
          >
            <Search className="w-4 h-4" />
            <span className="hidden sm:inline">Browse</span>
          </button>
          
          {/* Sessions Button */}
          <button
            onClick={() => {
              loadStoreSessions();
              setShowSessionDialog(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
          >
            <History className="w-4 h-4" />
            <span className="hidden sm:inline">Sessions</span>
          </button>

          {/* Delivery Person Selector - Show for delivery_out and delivery_return movements */}
          {(movementType === 'delivery_out' || movementType === 'delivery_return') && (
            <div className="flex items-center gap-2 ml-auto">
              <div className="min-w-[180px]">
                <DeliveryPersonSelector
                  selectedDeliveryHash={selectedDeliveryHash || undefined}
                                  onSelect={handleDeliveryPersonSelect}
                  placeholder="Select delivery person..."
                  showClearButton={true}
                />
              </div>
              
              {/* Delivery Person Items Buttons */}
              {selectedDeliveryHash && currentSession && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowDeliveryPersonItemListModal(true)}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-600 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 hover:border-green-300 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200"
                    title="Import delivery person items to session"
                  >
                    <Package className="w-4 h-4" />
                    <span className="hidden sm:inline">Import List</span>
                  </button>
                  <button
                    onClick={() => setShowDeliveryItemsModal(true)}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                    title="View delivery person's items (read-only)"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="hidden sm:inline">View</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-6 w-full">
        {/* Scanner & Cart Section - Full Width */}
        <div className="w-full space-y-3 lg:space-y-6">
          {/* Barcode Scanner - Compact on Mobile */}
          <div className="card w-full lg:p-6 p-4">
            <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-3 lg:mb-4 flex items-center gap-2">
              <Scan className="w-4 h-4 lg:w-5 lg:h-5" />
              <span className="lg:inline hidden">Item Scanner</span>
              <span className="lg:hidden inline">Scanner</span>
            </h3>
            
            <form onSubmit={handleBarcodeSubmit} className="space-y-4">
              <div className="flex gap-3">
                <div className="flex-1">
                  <input
                    ref={barcodeInputRef}
                    type="text"
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    onFocus={() => {
                      // Clear messages when focusing on scan field
                      setError(null);
                      setSuccess(null);
                    }}
                    placeholder="Scan barcode or enter SKU..."
                    className="input-field text-lg font-mono"
                    disabled={loading}
                />
              </div>
                <button
                  type="submit"
                  disabled={loading || !barcode.trim()}
                  className="btn-primary flex items-center gap-2 px-6"
                >
                  <Barcode className="w-4 h-4" />
                  {loading ? 'Scanning...' : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={startCameraScanner}
                  disabled={loading || cameraLoading}
                  className="btn-secondary flex items-center gap-2 px-4"
                  title="Scan barcode with camera"
                >
                  <Camera className="w-4 h-4" />
                  {cameraLoading ? 'Starting...' : 'Camera'}
                </button>
              </div>
            </form>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                {success}
              </div>
            )}
          </div>

          {/* Shopping Cart - Ultra Minimal */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden w-full flex-1">
            {/* Summary at Top - Only show when cart has items */}
            {cart.length > 0 && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <ShoppingCart className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {getTotalItems()} items • MVR {getTotalAmount().toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {currentStore?.name}
                        {sessionDeliveryPersonItemsCount > 0 && (
                          <span className="ml-2 text-blue-600 font-medium">
                            +{sessionDeliveryPersonItemsCount} delivery
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={saveTransaction}
                      disabled={saving || !cart.length}
                      className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1 transition-colors"
                    >
                      <Save className="w-3 h-3" />
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={clearCart}
                      className="text-gray-400 hover:text-red-500 p-1 rounded transition-colors"
                      title="Clear cart"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Simple Header for Empty Cart */}
            {cart.length === 0 && (
              <div className="p-3 border-b border-gray-100">
                <div className="flex items-center gap-2 text-gray-500">
                  <ShoppingCart className="w-4 h-4" />
                  <span className="text-sm font-medium">Shopping Cart</span>
                  {currentStore && (
                    <span className="text-xs">• {currentStore.name}</span>
                  )}
                </div>
              </div>
            )}

            {/* Cart Content */}
            {cart.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Package className="w-8 h-8 text-gray-300" />
                </div>
                <p className="text-sm">Scan items to add them</p>
              </div>
            ) : (
              <div className="max-h-[65vh] overflow-y-auto">
                <div className="divide-y divide-gray-100">
                  {cart.map((item) => (
                    <div key={item.barcode} className={`${getItemVisualFeedback(item.barcode)} p-3 relative hover:bg-gray-50 transition-colors`}>
                      {/* Visual Feedback Indicator */}
                      {(recentlyAddedItems.has(item.barcode) || recentlyUpdatedItems.has(item.barcode)) && (
                        <div className="absolute -top-1 -left-1 z-10">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold animate-bounce ${
                            recentlyAddedItems.has(item.barcode) ? 'bg-green-500' : 'bg-blue-500'
                          }`}>
                            {recentlyAddedItems.has(item.barcode) ? '✓' : '↑'}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-3 w-full">

                      {/* Stock Level Indicator Badge */}
                      {(() => {
                        const afterStock = getStockImpact(item.current_stock, item.cartQuantity);
                        const isRecentAction = recentlyAddedItems.has(item.barcode) || recentlyUpdatedItems.has(item.barcode);
                        
                        if (!isRecentAction && (afterStock < 0 || afterStock === 0 || afterStock <= 5)) {
                          return (
                            <div className="absolute -top-1 -right-1 z-10">
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                                afterStock < 0 ? 'bg-red-500' : afterStock === 0 ? 'bg-orange-500' : 'bg-yellow-500'
                              }`}>
                                {afterStock < 0 ? '!' : afterStock === 0 ? '0' : afterStock}
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })()}

                        {/* Product Image */}
                        <div className="w-10 h-10 flex-shrink-0">
                          {item.images && item.images.length > 0 ? (
                            <img 
                              src={item.images.find(img => img.is_primary)?.image_url || item.images[0]?.image_url}
                              alt={item.name}
                              className="w-10 h-10 rounded-lg object-cover border border-gray-200 cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => openImageModal(
                                item.images.find(img => img.is_primary)?.image_url || item.images[0]?.image_url,
                                item.name
                              )}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/images/btlogo.jpg';
                              }}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center">
                              <Package className="w-5 h-5 text-gray-400" />
                            </div>
                          )}
                        </div>
                        
                        {/* Item Details - Minimal */}
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 text-sm truncate">{item.name}</h4>
                          {/* Shelf Tags */}
                       {item.shelves && item.shelves.length > 0 && (
                         <div className="flex flex-wrap gap-1 mt-1">
                           {item.shelves.slice(0, 2).map((shelf, index) => (
                             <span 
                               key={shelf.id || index}
                               className="inline-block bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded-full font-medium"
                             >
                               {shelf.shelf_name}
                             </span>
                           ))}
                           {item.shelves.length > 2 && (
                             <span className="text-xs text-purple-600">
                               +{item.shelves.length - 2}
                             </span>
                           )}
                         </div>
                       )}
                          <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                            <span>Stock: {item.current_stock}</span>
                            <span className={`font-medium ${
                              getStockImpact(item.current_stock, item.cartQuantity) < 0 
                                ? 'text-red-600' 
                                : getStockImpact(item.current_stock, item.cartQuantity) <= 5 
                                  ? 'text-orange-600' 
                                  : 'text-green-600'
                            }`}>
                              → {getStockImpact(item.current_stock, item.cartQuantity)}
                            </span>
                          </div>
                        </div>
                    
                        {/* Quantity Controls - Minimal */}
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateCartItemQuantity(item.barcode, item.cartQuantity - 1)}
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 touch-manipulation"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className={`${getQuantityFeedback(item.barcode)} min-w-[1.5rem] text-center font-semibold text-sm`}>{item.cartQuantity}</span>
                          <button
                            onClick={() => updateCartItemQuantity(item.barcode, item.cartQuantity + 1)}
                            className="w-7 h-7 flex items-center justify-center rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 touch-manipulation"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        
                        {/* Total Price - Minimal */}
                        <div className="text-right">
                          <div className="font-semibold text-gray-900 text-sm">
                            MVR {item.totalAmount.toFixed(2)}
                          </div>
                        </div>

                        {/* Remove Button - Minimal */}
                        <button
                          onClick={() => removeCartItem(item.barcode)}
                          className="w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 touch-manipulation flex-shrink-0"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Summary & Actions Section - Full Width */}
        <div className="w-full space-y-6">
          {/* Delivery Person Scanner View - Show when items are imported */}
          {currentSession && sessionDeliveryPersonItemsCount > 0 && (
            <DeliveryPersonScannerView
              sessionId={currentSession.id}
              cartItems={cart}
              onItemClick={(item) => {
                // Focus on the barcode input and pre-fill with item SKU for quick scanning
                setBarcode(item.item_sku);
                if (barcodeInputRef.current) {
                  barcodeInputRef.current.focus();
                  barcodeInputRef.current.select();
                }
              }}
            />
          )}

          {/* Transaction Summary */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Movement Summary</h3>
            
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Items:</span>
                <span className="font-medium">{getTotalItems()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Line Items:</span>
                <span className="font-medium">{cart.length}</span>
              </div>
              
              {/* Stock Impact Warning */}
              {cart.some(item => getStockImpact(item.current_stock, item.cartQuantity) < 0) && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-xs text-red-700 font-medium mb-1">⚠️ Stock Warning</p>
                  <p className="text-xs text-red-600">
                    Some items will have negative stock after this movement
                  </p>
                </div>
              )}
              
              {/* Low Stock Warning */}
              {cart.some(item => getStockImpact(item.current_stock, item.cartQuantity) >= 0 && getStockImpact(item.current_stock, item.cartQuantity) <= 5) && (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-xs text-orange-700 font-medium mb-1">📦 Low Stock Alert</p>
                  <p className="text-xs text-orange-600">
                    Some items will have low stock (≤5) after this movement
                  </p>
                </div>
              )}
              
              <hr className="border-gray-200" />
              <div className="flex justify-between text-lg font-semibold">
                <span>Total Value:</span>
                <span className="text-green-600">MVR {getTotalAmount().toFixed(2)}</span>
              </div>
            </div>

            {/* Movement Remarks */}
            <div className="mt-4">
              <label htmlFor="movementRemarks" className="block text-sm font-medium text-gray-700 mb-2">
                Movement Remarks (Optional)
              </label>
              <textarea
                id="movementRemarks"
                value={movementRemarks}
                onChange={(e) => setMovementRemarks(e.target.value)}
                placeholder="Enter remarks for this movement..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                rows={2}
                maxLength={200}
              />
              <div className="text-xs text-gray-500 mt-1 text-right">
                {movementRemarks.length}/200 characters
              </div>
            </div>

            <button
              onClick={saveTransaction}
              disabled={cart.length === 0 || saving || !currentStore}
              className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Movement'}
            </button>
          </div>

          {/* Session & Store Info */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Session Info</h3>
            {currentStore ? (
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Store:</span>
                  <span className="font-medium">{currentStore.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Movement Type:</span>
                  <span className="flex items-center gap-1">
                    <span>{getMovementTypeConfig().icon}</span>
                    <span className="font-medium text-xs">{getMovementTypeConfig().label}</span>
                  </span>
                </div>
                
                {/* Delivery Person Selection - Only for delivery movements */}
                {(movementType === 'delivery_out' || movementType === 'delivery_return') && (
                  <div className="space-y-2">
                    <div className="flex justify-between items-start">
                      <span className="text-gray-600 text-sm">
                        Delivery Person:
                        {movementType === 'delivery_out' && <span className="text-red-500"> *</span>}
                      </span>
                    </div>
                    <DeliveryPersonSelector
                      selectedDeliveryHash={selectedDeliveryHash || undefined}
                      onSelect={handleDeliveryPersonSelect}
                      placeholder={
                        movementType === 'delivery_out' 
                          ? "Select delivery person..." 
                          : "Select delivery person (optional)..."
                      }
                      showClearButton={true}
                    />
                    {selectedDeliveryPerson && (
                      <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            {selectedDeliveryPerson.avatar && (
                              <img
                                src={selectedDeliveryPerson.avatar}
                                alt={selectedDeliveryPerson.name}
                                className="w-8 h-8 rounded-full object-cover"
                              />
                            )}
                            <div>
                              <div className="text-sm font-medium text-blue-900">
                                {selectedDeliveryPerson.displayName}
                              </div>
                              {selectedDeliveryPerson.phone && (
                                <div className="text-xs text-blue-700">
                                  📞 {selectedDeliveryPerson.phone}
                                </div>
                              )}
                            </div>
                          </div>
                          <button
                            onClick={() => setShowDeliveryItemsModal(true)}
                            className="px-3 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-1"
                            title="View delivery person's item list"
                          >
                            <Package className="w-3 h-3" />
                            <span>View Items</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-gray-600">User:</span>
                  <span className="font-medium">{user?.name}</span>
                </div>
                {currentSession ? (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Session:</span>
                    <span className="font-medium text-xs">{currentSession.session_name}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span>Session will be created when adding first item</span>
                  </div>
                )}
                {sessionLoading && (
                  <div className="flex items-center gap-2 text-xs text-blue-600">
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    <span>Creating session...</span>
                </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No store selected</p>
            )}
          </div>
        </div>

      {/* Movement History - Always Visible */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <History className="w-5 h-5" />
          Recent Movement Entries
          </h3>
        
        {recentMovements.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No recent movement entries found</p>
          </div>
        ) : (
        <div className="space-y-3">
            {recentMovements.map((movement) => (
              <div 
                key={movement.movement_id} 
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 hover:border-gray-300 cursor-pointer transition-all duration-200"
                onClick={() => handleMovementClick(movement)}
              >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 mb-1">
                    {movement.reference_number}
                </p>
                <p className="text-xs text-gray-500">
                    {movement.total_items} items • MVR {movement.total_amount.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(movement.movement_date).toLocaleDateString()} • {movement.created_by_name}
                </p>
                {/* Show delivery person info if available */}
                {movement.delivery_person && (
                  <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                    🚚 {movement.delivery_person.name} ({movement.delivery_person.code})
                  </p>
                )}
              </div>
                <div className="text-right flex items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    movement.movement_type === 'purchase_in' ? 'bg-green-100 text-green-800' :
                    movement.movement_type === 'adjustment' ? 'bg-blue-100 text-blue-800' :
                    movement.movement_type === 'delivery_out' ? 'bg-orange-100 text-orange-800' :
                    movement.movement_type === 'delivery_return' ? 'bg-purple-100 text-purple-800' :
                    movement.movement_type === 'opening_stock' ? 'bg-indigo-100 text-indigo-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {movement.movement_type === 'purchase_in' ? 'Purchase In' :
                     movement.movement_type === 'adjustment' ? 'Adjustment' :
                     movement.movement_type === 'delivery_out' ? 'Delivery Out' :
                     movement.movement_type === 'delivery_return' ? 'Delivery Return' :
                     movement.movement_type === 'opening_stock' ? 'Opening Stock' :
                     movement.movement_type.replace('_', ' ').toUpperCase()}
                  </span>
                  <Eye className="w-4 h-4 text-gray-400" />
                </div>
            </div>
          ))}
        </div>
        )}
      </div>

      {/* Image Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75">
          <div className="relative max-w-4xl max-h-full bg-white rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedImageItem}
              </h3>
              <button
                onClick={closeImageModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <img 
                src={selectedImage}
                alt={selectedImageItem || 'Product Image'}
                className="max-w-full max-h-96 mx-auto rounded-lg object-contain"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/images/btlogo.jpg';
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Item List Dialog */}
      {showItemListDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-4xl max-h-full bg-white rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Available Items - {currentStore?.name}
              </h3>
              <button
                onClick={() => setShowItemListDialog(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={itemSearchTerm}
                  onChange={(e) => setItemSearchTerm(e.target.value)}
                  placeholder="Search items by name, SKU, or barcode..."
                  className="input-field pl-10"
                />
              </div>
            </div>
            
            <div className="max-h-96 overflow-y-auto p-4">
              {itemListLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading items...</p>
                </div>
              ) : availableItems.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No items found</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {availableItems.map((item) => (
                    <div key={item.id} className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-colors">
                      <div className="flex items-center gap-3">
                        {/* Item Image */}
                        <div className="w-12 h-12 flex-shrink-0">
                          {item.images && item.images.length > 0 ? (
                            <img 
                              src={item.images.find(img => img.is_primary)?.image_url || item.images[0]?.image_url}
                              alt={item.name}
                              className="w-12 h-12 rounded-lg object-cover border border-gray-200 cursor-pointer"
                              onClick={() => openImageModal(
                                item.images.find(img => img.is_primary)?.image_url || item.images[0]?.image_url,
                                item.name
                              )}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = '/images/btlogo.jpg';
                              }}
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 text-sm truncate">{item.name}</h4>
                          <p className="text-xs text-gray-500">SKU: {item.sku}</p>
                          <p className="text-xs text-gray-400">Stock: {item.current_stock}</p>
                        </div>
                        
                        <button
                          onClick={async () => await addItemFromList(item)}
                          className="btn-primary text-xs px-2 py-1 flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" />
                          Add
                        </button>
                      </div>
            </div>
          ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Session Management Dialog */}
      {showSessionDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Session Management</h2>
                  <p className="text-sm text-gray-500">Manage ongoing sessions for {currentStore?.name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowCreateSessionDialog(true)}
                    className="btn-primary flex items-center gap-2 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    New Session
                  </button>
                  <button
                    onClick={() => setShowSessionDialog(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Current Session Info */}
              {currentSession && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-blue-900 flex items-center gap-2">
                        <span>🔄</span>
                        Current Active Session
                      </h3>
                      <p className="text-sm text-blue-700 mt-1">
                        {currentSession.session_name} • {getMovementTypeConfig().label} • {cart.length} items
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleClearSessionItems(currentSession.id)}
                        className="btn-secondary-sm text-orange-600 hover:bg-orange-50"
                      >
                        Clear Items
                      </button>
                      <button
                        onClick={() => handleCloseSession(currentSession.id)}
                        className="btn-secondary-sm text-red-600 hover:bg-red-50"
                      >
                        Close Session
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {sessionsLoading ? (
                <div className="flex justify-center items-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
                  <span className="ml-2 text-gray-600">Loading sessions...</span>
                </div>
              ) : storeSessions.length === 0 ? (
                <div className="text-center py-8">
                  <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No active sessions found for this store</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {storeSessions.map((session) => (
                    <div key={session.id} className={`border rounded-lg p-4 ${
                      session.is_active ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
                    } ${
                      currentSession?.id === session.id ? 'ring-2 ring-blue-300' : ''
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">
                              {
                                session.movement_type === 'purchase_in' ? '📦' :
                                session.movement_type === 'adjustment' ? '⚖️' :
                                session.movement_type === 'delivery_out' ? '🚚' :
                                session.movement_type === 'delivery_return' ? '↩️' :
                                session.movement_type === 'opening_stock' ? '🏗️' : '📋'
                              }
                            </span>
                            <h3 className="font-medium text-gray-900">
                              {
                                session.movement_type === 'purchase_in' ? 'Purchase In' :
                                session.movement_type === 'adjustment' ? 'Stock Adjustment' :
                                session.movement_type === 'delivery_out' ? 'Delivery Out' :
                                session.movement_type === 'delivery_return' ? 'Delivery Return' :
                                session.movement_type === 'opening_stock' ? 'Opening Stock' : session.movement_type
                              }
                            </h3>
                            <div className="flex items-center gap-2">
                              {session.is_own_session && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Your Session</span>
                              )}
                              {currentSession?.id === session.id && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
                              )}
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                session.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {session.status_badge}
                              </span>
                            </div>
                          </div>
                          <div className="text-sm text-gray-600 space-y-1">
                            <div className="flex items-center gap-4">
                              <p>👤 <span className="font-medium">{session.creator?.phone || `User ${session.creator?.id?.slice(0,8)}`}</span></p>
                              <p>📅 <span className="font-medium">{session.formatted_date}</span></p>
                            </div>
                            <p>📦 Items: <span className="font-medium">{session.total_items}</span> | 💰 Total: <span className="font-medium">MVR {Number(session.total_amount).toFixed(2)}</span></p>
                            {session.remarks && (
                              <p className="text-xs text-gray-500">💭 {session.remarks}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => loadSharedSession(session.id)}
                            className="btn-primary flex items-center gap-2 text-sm"
                            disabled={currentSession?.id === session.id}
                          >
                            <Eye className="w-4 h-4" />
                            {currentSession?.id === session.id ? 'Current' : 'Load'}
                          </button>
                          {session.is_active && (
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleClearSessionItems(session.id)}
                                className="btn-secondary-sm text-orange-600 hover:bg-orange-50 text-xs"
                                title="Clear all items"
                              >
                                🗑️
                              </button>
                              <button
                                onClick={() => handleCloseSession(session.id)}
                                className="btn-secondary-sm text-red-600 hover:bg-red-50 text-xs"
                                title="Close session"
                              >
                                ❌
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
        </div>
      </div>
      )}

      {/* Create New Session Dialog */}
      {showCreateSessionDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Create New Session</h2>
                <button
                  onClick={() => {
                    setShowCreateSessionDialog(false);
                    setNewSessionRemarks('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Movement Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Movement Type</label>
                  <select
                    value={newSessionMovementType}
                    onChange={(e) => setNewSessionMovementType(e.target.value as any)}
                    className="input-field"
                  >
                    <option value="delivery_out">🚚 Delivery Out</option>
                    <option value="purchase_in">📦 Purchase In</option>
                    <option value="adjustment">⚖️ Stock Adjustment</option>
                    <option value="delivery_return">↩️ Delivery Return</option>
                    <option value="opening_stock">🏗️ Opening Stock</option>
                  </select>
                </div>

                {/* Session Remarks */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Session Notes (Optional)</label>
                  <textarea
                    value={newSessionRemarks}
                    onChange={(e) => setNewSessionRemarks(e.target.value)}
                    placeholder="Add notes about this session..."
                    className="input-field resize-none"
                    rows={3}
                  />
                </div>

                {/* Session Info Preview */}
                <div className="p-3 bg-gray-50 rounded-lg border">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Session Preview</h4>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p>👤 User: {user?.name || user?.phone}</p>
                    <p>🏪 Store: {currentStore?.name}</p>
                    <p>📅 Created: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</p>
                    <p>📋 Type: {[
                      { value: 'delivery_out', label: '🚚 Delivery Out' },
                      { value: 'purchase_in', label: '📦 Purchase In' },
                      { value: 'adjustment', label: '⚖️ Stock Adjustment' },
                      { value: 'delivery_return', label: '↩️ Delivery Return' },
                      { value: 'opening_stock', label: '🏗️ Opening Stock' }
                    ].find(t => t.value === newSessionMovementType)?.label}</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowCreateSessionDialog(false);
                    setNewSessionRemarks('');
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    await createNewSession(newSessionMovementType, newSessionRemarks);
                    setShowCreateSessionDialog(false);
                    setNewSessionRemarks('');
                    await loadStoreSessions(); // Refresh sessions list
                  }}
                  disabled={sessionLoading}
                  className="btn-primary"
                >
                  {sessionLoading ? 'Creating...' : 'Create Session'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Movement Details Dialog */}
      {showMovementDetailsDialog && selectedMovement && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Movement Details</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedMovement.reference_number} • {selectedMovement.movement_type.replace('_', ' ').toUpperCase()}
                </p>
              </div>
              <button
                onClick={closeMovementDetailsDialog}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/50 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {movementDetailsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading movement details...</span>
                </div>
              ) : movementDetails ? (
                <div className="space-y-6">
                  {/* Movement Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Date & Time</label>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {new Date(movementDetails.movement.movement_date).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(movementDetails.movement.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Store</label>
                        <p className="text-sm font-medium text-gray-900 mt-1">{movementDetails.movement.store_name}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Created By</label>
                        <p className="text-sm font-medium text-gray-900 mt-1">{movementDetails.movement.created_by_name}</p>
                      </div>
                    </div>
                    
                    {/* Delivery Person Info (if available) */}
                    {selectedMovement.delivery_person && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Delivery Person</label>
                        <div className="flex items-center gap-3">
                          {selectedMovement.delivery_person.avatar_url && (
                            <img 
                              src={selectedMovement.delivery_person.avatar_url} 
                              alt={selectedMovement.delivery_person.name}
                              className="w-8 h-8 rounded-full object-cover"
                            />
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedMovement.delivery_person.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              Code: {selectedMovement.delivery_person.code}
                              {selectedMovement.delivery_person.phone && ` • ${selectedMovement.delivery_person.phone}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {movementDetails.movement.remarks && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">Remarks</label>
                        <p className="text-sm text-gray-700 mt-1">{movementDetails.movement.remarks}</p>
                      </div>
                    )}
                  </div>

                  {/* Summary */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-900">Total Items: {movementDetails.total_items}</p>
                        <p className="text-lg font-bold text-blue-900">MVR {parseFloat(movementDetails.total_amount).toFixed(2)}</p>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                          selectedMovement.movement_type === 'purchase_in' ? 'bg-green-100 text-green-800' :
                          selectedMovement.movement_type === 'adjustment' ? 'bg-blue-100 text-blue-800' :
                          selectedMovement.movement_type === 'delivery_out' ? 'bg-orange-100 text-orange-800' :
                          selectedMovement.movement_type === 'delivery_return' ? 'bg-purple-100 text-purple-800' :
                          selectedMovement.movement_type === 'opening_stock' ? 'bg-indigo-100 text-indigo-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {selectedMovement.movement_type === 'purchase_in' ? 'Purchase In' :
                           selectedMovement.movement_type === 'adjustment' ? 'Adjustment' :
                           selectedMovement.movement_type === 'delivery_out' ? 'Delivery Out' :
                           selectedMovement.movement_type === 'delivery_return' ? 'Delivery Return' :
                           selectedMovement.movement_type === 'opening_stock' ? 'Opening Stock' :
                           selectedMovement.movement_type.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Items List */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">Items</h4>
                    <div className="space-y-3">
                      {movementDetails.items.map((item: any, index: number) => (
                        <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start gap-4">
                            {/* Item Image */}
                            <div className="w-16 h-16 bg-gray-100 rounded-lg flex-shrink-0 overflow-hidden">
                              {item.item_images && item.item_images.length > 0 ? (
                                <img 
                                  src={item.item_images[0].image_url} 
                                  alt={item.item_name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                            </div>

                            {/* Item Details */}
                            <div className="flex-1 min-w-0">
                              <h5 className="text-sm font-medium text-gray-900 mb-1">{item.item_name}</h5>
                              <p className="text-xs text-gray-500 mb-2">SKU: {item.item_sku} • Barcode: {item.item_barcode}</p>
                              
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                <div>
                                  <span className="text-gray-500">Quantity:</span>
                                  <span className="ml-1 font-medium text-gray-900">{item.quantity}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Unit Price:</span>
                                  <span className="ml-1 font-medium text-gray-900">MVR {parseFloat(item.unit_price).toFixed(2)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Unit Cost:</span>
                                  <span className="ml-1 font-medium text-gray-900">MVR {parseFloat(item.unit_cost).toFixed(2)}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500">Total:</span>
                                  <span className="ml-1 font-medium text-blue-600">MVR {parseFloat(item.total_amount).toFixed(2)}</span>
                                </div>
                              </div>

                              {item.order_references && (
                                <p className="text-xs text-gray-400 mt-2">Ref: {item.order_references}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-red-600 mb-2">Failed to load movement details</div>
                  <button 
                    onClick={() => handleMovementClick(selectedMovement)}
                    className="text-blue-600 hover:text-blue-700 text-sm"
                  >
                    Try again
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={closeMovementDetailsDialog}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Person Items Modal */}
      {selectedDeliveryPerson && selectedDeliveryHash && (
        <DeliveryPersonItemsModal
          isOpen={showDeliveryItemsModal}
          onClose={() => setShowDeliveryItemsModal(false)}
          deliveryPerson={selectedDeliveryPerson}
          deliveryHash={selectedDeliveryHash}
        />
      )}

      {/* Delivery Person Item List Modal */}
      {selectedDeliveryHash && currentSession && (
        <DeliveryPersonItemListModal
          isOpen={showDeliveryPersonItemListModal}
          onClose={() => setShowDeliveryPersonItemListModal(false)}
          sessionId={currentSession.id}
          deliveryHash={selectedDeliveryHash}
          cartItems={cart}
          onItemsImported={(count) => {
            setSuccess(`Successfully imported ${count} delivery person items to session`);
            // Auto-close success message after 3 seconds
            setTimeout(() => setSuccess(null), 3000);
            // Refresh the delivery person items count
            loadSessionDeliveryPersonItemsCount();
          }}
          onSessionCleared={handleSessionCleared}
        />
      )}

      {/* Camera Scanner Modal */}
      {showCameraScanner && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Camera Barcode Scanner</h3>
                <button
                  onClick={stopCameraScanner}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {cameraError ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Camera className="w-8 h-8 text-red-500" />
                  </div>
                  <p className="text-red-600 text-sm mb-4">{cameraError}</p>
                  <button
                    onClick={startCameraScanner}
                    className="btn-primary text-sm"
                  >
                    Try Again
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="relative bg-black rounded-lg overflow-hidden" style={{ aspectRatio: '4/3' }}>
                    <video
                      ref={videoRef}
                      className="w-full h-full object-cover"
                      autoPlay
                      playsInline
                    />
                    {cameraLoading && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <div className="text-white text-center">
                          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                          <p className="text-sm">Starting camera...</p>
                        </div>
                      </div>
                    )}
                    {/* Scanning overlay */}
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-32 border-2 border-green-400 rounded-lg">
                        <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-green-400"></div>
                        <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-green-400"></div>
                        <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-green-400"></div>
                        <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-green-400"></div>
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">Position the barcode within the green frame</p>
                    <p className="text-xs text-gray-500">The scanner will automatically detect and scan barcodes</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MovementRecorder;
export {}; 